import React, { useMemo, useRef, useState } from "react";
import {
  TextInput,
  StyleSheet,
  ViewStyle,
  StyleProp,
  View,
  Animated,
  TouchableOpacity,
  Platform,
  TextInputProps,
} from "react-native";

import { useFocusEffect } from "@react-navigation/core";
import { AHText, AHTextFonts, Box, ColorType, useTheme, AHIcon } from "arkh";
import { FormattedMessage } from "react-intl";
import { BottomSheetTextInput } from "@gorhom/bottom-sheet";

type AHSearchBarProps = {
  searchText?: string;
  setSearchText: (text: string) => void;
  placeholder?: string;
  style?: StyleProp<ViewStyle>;
  cancelColor?: ColorType;
  onCancel?: () => void;
  onFocus?: TextInputProps["onFocus"];
  onBlur?: TextInputProps["onBlur"];
  autoCapitalize?: TextInputProps["autoCapitalize"];
  color?: ColorType;
  inBottomSheet?: boolean;
};

const AnimatedTouchable = Animated.createAnimatedComponent(TouchableOpacity);

const ClearButton = ({ onPress }: { onPress: () => void }) => {
  return (
    <TouchableOpacity
      activeOpacity={1}
      pressRetentionOffset={{
        top: 20,
        bottom: 20,
        left: 20,
        right: 20,
      }}
      hitSlop={{
        top: 20,
        bottom: 20,
        left: 20,
        right: 20,
      }}
      style={{
        borderRadius: 8,
        backgroundColor: "rgb(139,137,135)",
        height: 16,
        width: 16,
        borderWidth: 0,
        alignItems: "center",
        justifyContent: "center",
      }}
      onPress={onPress}
    >
      <View style={{ marginLeft: Platform.OS === "ios" ? StyleSheet.hairlineWidth : 0 }}>
        <AHIcon type="clear" customColor="white" forceSize={12} />
      </View>
    </TouchableOpacity>
  );
};

export const MSearchBar = ({
  searchText,
  setSearchText,
  placeholder,
  style,
  cancelColor,
  onCancel,
  onFocus,
  onBlur,
  autoCapitalize,
  color = "surface",
  inBottomSheet,
}: AHSearchBarProps) => {
  const theme = useTheme();
  const inputRef = useRef<TextInput>(null);
  useFocusEffect(
    React.useCallback(() => {
      return () => {
        inputRef.current?.blur();
      };
    }, [inputRef])
  );
  const [isSearching, setIsSearching] = useState(false);
  const isEmpty = searchText === "";

  const clear = () => {
    setSearchText("");
  };

  const InnerTextInput = useMemo(
    () => (inBottomSheet ? BottomSheetTextInput : TextInput),
    [inBottomSheet]
  );

  return (
    <View style={[styles.wrapper, style]}>
      <View style={[styles.container, { backgroundColor: theme[color] }]}>
        <Box
          style={{ width: 40, alignItems: "center", justifyContent: "center" }}
        >
          <AHIcon type="search" forceSize={18} customColor="rgb(124,124,129)" />
        </Box>
        <InnerTextInput
          ref={inputRef as any}
          placeholderTextColor="rgb(162,162,167)"
          value={searchText}
          onChangeText={setSearchText}
          style={styles.textInput}
          placeholder={placeholder}
          returnKeyType="search"
          autoCapitalize={autoCapitalize}
          onFocus={(e) => {
            onFocus?.(e);
            setIsSearching(true);
          }}
          onBlur={(e) => {
            onBlur?.(e);
            setIsSearching(false);
          }}
        />
        <Animated.View
          style={{
            width: 40,
            justifyContent: "center",
            alignItems: "center",
            opacity: isEmpty ? 0 : 1,
          }}
        >
          <ClearButton onPress={clear} />
        </Animated.View>
      </View>
      <AnimatedTouchable
        style={{
          alignItems: "flex-end",
          width: isSearching ? 65 : 0,
        }}
        onPress={() => {
          clear();
          inputRef.current?.blur();
          onCancel?.();
        }}
      >
        <AHText
          style={{
            color: theme[cancelColor ?? "primary"],
            fontSize: 16,
            maxHeight: 20,
          }}
        >
          {isSearching ? (
            <FormattedMessage
              defaultMessage="Cancel"
              description="MSearchBar cancel button"
            />
          ) : (
            ""
          )}
        </AHText>
      </AnimatedTouchable>
    </View>
  );
};

MSearchBar.layout = { height: 36 };

const styles = StyleSheet.create({
  wrapper: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
  container: {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    flex: 1,
    height: MSearchBar.layout.height,
    borderRadius: MSearchBar.layout.height / 2,
  },
  textInput: {
    flex: 1,
    fontFamily: AHTextFonts.REGULAR,
    fontSize: 16,
    height: MSearchBar.layout.height,
  },
});
